import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowUp, BarChart3, Brain, Check, ChevronDown, ChevronRight, ChevronUp, Download, FileSpreadsheet, FileText,
  Folder, History, LayoutGrid, Maximize2, Menu, Minus, MoreHorizontal, MousePointer2, Newspaper, PanelLeftClose, Pause,
  Pencil, Play, Plus, Puzzle, RotateCcw, Send, Share2, ShieldCheck, SquarePen, Users, Wrench, X,
} from 'lucide-react';
import type {
  DataScene, DocumentScene, LandingCopy, PluginScene, ResearchScene, SceneCopy, StepCopy,
} from './content';

/* ------------------------------------------------------------------ */
/* Playback                                                            */
/* ------------------------------------------------------------------ */

const prefersReducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/** Where a paused, reduced-motion demo rests: every scene is complete. */
export const RESTING_PROGRESS = 0.92;

/** Playback is local: no account, microphone or model request is used by a demo. */
export function usePlayback(duration: number, onCycle?: () => void) {
  const [paused, setPaused] = useState(prefersReducedMotion);
  const [elapsed, setElapsed] = useState(() => prefersReducedMotion() ? duration * RESTING_PROGRESS : 0);
  const [visible, setVisible] = useState(false);
  const [foreground, setForeground] = useState(() => !document.hidden);
  const ref = useRef<HTMLDivElement>(null);
  const clock = useRef(elapsed);
  const cycle = useRef(onCycle);
  cycle.current = onCycle;
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!('IntersectionObserver' in window)) { setVisible(true); return; }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.15 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const handleVisibility = () => setForeground(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const handleMotion = () => { if (media?.matches) { setPaused(true); clock.current = duration * RESTING_PROGRESS; setElapsed(clock.current); } };
    media?.addEventListener('change', handleMotion);
    return () => { document.removeEventListener('visibilitychange', handleVisibility); media?.removeEventListener('change', handleMotion); };
  }, [duration]);
  useEffect(() => {
    if (paused || !visible || !foreground) return;
    const timer = window.setInterval(() => {
      clock.current += 100;
      if (clock.current >= duration) { clock.current %= duration; cycle.current?.(); }
      setElapsed(clock.current);
    }, 100);
    return () => window.clearInterval(timer);
  }, [duration, foreground, paused, visible]);
  const restart = useCallback((showResult = false) => { clock.current = showResult ? duration * RESTING_PROGRESS : 0; setElapsed(clock.current); }, [duration]);
  return { ref, paused, running: !paused && visible && foreground, toggle: () => setPaused(value => !value), setPaused, restart, progress: elapsed / duration };
}
type Playback = ReturnType<typeof usePlayback>;

/** Scales a fixed-size product canvas to its container, like a screenshot would. */
type Layout = 'wide' | 'medium' | 'narrow';
const LAYOUTS: Record<Layout, { width: number; height: number }> = {
  wide: { width: 1200, height: 720 },
  medium: { width: 860, height: 660 },
  narrow: { width: 420, height: 700 },
};
function useFit() {
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<{ layout: Layout; scale: number }>({ layout: 'wide', scale: 1 });
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => {
      const width = node.clientWidth || 1200;
      const layout: Layout = width >= 980 ? 'wide' : width >= 600 ? 'medium' : 'narrow';
      const scale = Math.min(1, width / LAYOUTS[layout].width);
      setFit(prev => prev.layout === layout && Math.abs(prev.scale - scale) < 0.002 ? prev : { layout, scale });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, ...fit };
}

/* ------------------------------------------------------------------ */
/* Scene timing                                                        */
/* ------------------------------------------------------------------ */

const T = { typeEnd: 0.13, send: 0.15, stepsStart: 0.18, stepsEnd: 0.52, answer: 0.6 };
/** 0 = composing, 1 = the agent is working, 2 = the result is shown. */
export function phaseOf(progress: number) { return progress < T.send ? 0 : progress < T.answer ? 1 : 2; }
const at = (progress: number, time: number) => progress >= time;
/** Returns how many of `count` items are visible, spread across a window. */
const revealed = (progress: number, count: number, start: number, end: number) =>
  progress < start ? 0 : Math.min(count, 1 + Math.floor(((progress - start) / Math.max(0.0001, end - start)) * count));

/* ------------------------------------------------------------------ */
/* Small product primitives (mirror the signed-in app)                  */
/* ------------------------------------------------------------------ */

function Toggle({ on }: { on?: boolean }) { return <span className={`pw-toggle ${on ? 'is-on' : ''}`}><i /></span>; }

function Composer({ copy, text, state, compact }: { copy: LandingCopy; text: string; state: 'idle' | 'typing' | 'running'; compact?: boolean }) {
  return <div className="pw-composer">
    <div className="pw-composer-text">{text ? <span>{text}<i className="pw-caret" /></span> : <span className="is-placeholder">{copy.ui.placeholder}</span>}</div>
    <div className="pw-composer-row">
      <Plus size={18} strokeWidth={1.8} />
      {!compact && <span className="pw-select">Ask for approval<ChevronDown size={13} /></span>}
      {!compact && <span className="pw-select">Gemini 3.8 Flash<ChevronDown size={13} /></span>}
      <span className="pw-select">Auto<ChevronDown size={13} /></span>
      {!compact && <span className="pw-switch"><Toggle />Interactive</span>}
      <span className="pw-switch is-strong"><Toggle on />Reasoning</span>
      <span className={`pw-send is-${state}`}>{state === 'running' ? <i className="pw-stop" /> : <ArrowUp size={17} strokeWidth={2.2} />}</span>
    </div>
  </div>;
}

function ToolRow({ text }: { text: string }) {
  return <div className="pw-tool"><Wrench size={12} /><span>{text}</span><ChevronRight size={13} /></div>;
}

/** The step_progress timeline card of the integrated agent. */
function Timeline({ steps, progress, copy, start = T.stepsStart, end = T.stepsEnd }: {
  steps: StepCopy[]; progress: number; copy: LandingCopy; start?: number; end?: number;
}) {
  const shown = revealed(progress, steps.length, start, end);
  const slice = (end - start) / steps.length;
  if (progress >= T.answer) {
    return <span className="pw-chip"><b>{steps.length}</b>{copy.ui.done}<ChevronRight size={14} /></span>;
  }
  return <>
    <span className="pw-chip is-live"><b>{steps.length}</b>{copy.ui.steps.replace('{n}', String(steps.length))}<i className="pw-live-dot" /><ChevronDown size={14} /></span>
    <div className="pw-steps">
      {steps.slice(0, shown).map((step, index) => {
        const active = index === shown - 1;
        const tools = step.tools ?? [];
        const local = (progress - (start + index * slice)) / slice;
        const toolCount = active ? Math.min(tools.length, Math.max(0, Math.floor(local * (tools.length + 1)))) : tools.length;
        return <div key={step.title} className={`pw-step ${active ? 'is-active' : ''}`}>
          <div className="pw-step-title">{step.title}</div>
          <div className="pw-step-actor">{step.actor}</div>
          {step.body && <div className="pw-step-body">{step.body}</div>}
          {tools.slice(0, toolCount).map(tool => <ToolRow key={tool} text={tool} />)}
        </div>;
      })}
    </div>
  </>;
}

function UserMessage({ text, attachment }: { text: string; attachment?: string }) {
  return <div className="pw-user pw-enter">
    {attachment && <div className="pw-attachment"><span className="pw-file-icon is-xlsx"><FileSpreadsheet size={16} /></span><span><strong>{attachment}</strong><small>XLSX</small></span></div>}
    <div className="pw-bubble">{text}</div>
  </div>;
}

function AgentMessage({ children }: { children: React.ReactNode }) {
  return <div className="pw-agent pw-enter">
    <img src="/assets/agent_logo.webp" alt="" width="26" height="26" />
    <div className="pw-agent-card">{children}</div>
  </div>;
}

function Table({ head, rows, visible = rows.length }: { head: string[]; rows: string[][]; visible?: number }) {
  return <table className="pw-table"><thead><tr>{head.map(cell => <th key={cell}>{cell}</th>)}</tr></thead>
    <tbody>{rows.slice(0, visible).map(row => <tr key={row[0]} className="pw-enter">{row.map((cell, i) => <td key={i}>{cell}</td>)}</tr>)}</tbody></table>;
}

/* ------------------------------------------------------------------ */
/* Chat scenes                                                          */
/* ------------------------------------------------------------------ */

/** Keeps the newest message in view, as the app does while an answer streams. */
function useStickToBottom(progress: number) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { const node = ref.current; if (node) node.scrollTop = node.scrollHeight; }, [progress]);
  return ref;
}

function typed(prompt: string, progress: number) {
  if (progress >= T.send) return '';
  return prompt.slice(0, Math.ceil(prompt.length * Math.min(1, progress / T.typeEnd)));
}

function ChatScene({ scene, copy, progress, layout, children }: {
  scene: ResearchScene | DataScene | PluginScene; copy: LandingCopy; progress: number; layout: Layout; children: React.ReactNode;
}) {
  const sent = at(progress, T.send);
  const running = sent && progress < T.answer + 0.12;
  const thread = useStickToBottom(progress);
  return <div className="pw-chat">
    <div className="pw-topbar"><span className="pw-round"><Menu size={17} /></span><span className="pw-top-actions">{sent && <span className="pw-round"><Share2 size={16} /></span>}<span className="pw-round"><Users size={16} /></span>{sent && <span className="pw-round"><MoreHorizontal size={16} /></span>}</span></div>
    {!sent && <div className="pw-empty"><h3>Askyour<span>.agent</span></h3><p>{copy.ui.welcome}</p></div>}
    <div className="pw-thread" ref={thread}>
      {sent && <UserMessage text={scene.prompt} attachment={scene.id === 'data' ? scene.attachment : undefined} />}
      {at(progress, T.stepsStart - 0.01) && children}
    </div>
    <div className="pw-composer-dock">
      <Composer copy={copy} text={typed(scene.prompt, progress)} state={running ? 'running' : progress < T.send && progress > 0.005 ? 'typing' : 'idle'} compact={layout === 'narrow'} />
      <p className="pw-disclaimer">{copy.ui.disclaimer}</p>
    </div>
  </div>;
}

function ResearchResult({ scene, progress }: { scene: ResearchScene; progress: number }) {
  const rows = revealed(progress, scene.table.rows.length, 0.68, 0.76);
  return <div className="pw-answer">
    <p className="pw-enter">{scene.lead}</p>
    {at(progress, 0.65) && <h4 className="pw-enter">{scene.heading}</h4>}
    {at(progress, 0.67) && <Table head={scene.table.head} rows={scene.table.rows} visible={rows} />}
    {at(progress, 0.78) && <div className="pw-sources pw-enter"><span className="pw-favicons"><i /><i /><i /></span>{scene.sources}<ChevronRight size={13} /></div>}
  </div>;
}

function DataResult({ scene, progress }: { scene: DataScene; progress: number }) {
  const bars = revealed(progress, scene.series.length, 0.66, 0.74);
  const max = 120;
  return <div className="pw-answer">
    <p className="pw-enter">{scene.lead}</p>
    {at(progress, 0.64) && <figure className="pw-chart pw-enter">
      <figcaption>{scene.chartTitle}</figcaption>
      <div className="pw-chart-plot">
        <i className="pw-chart-target" style={{ bottom: `${(100 / max) * 100}%` }}><span>{scene.target}</span></i>
        {scene.series.map(([label, value], index) => <div key={label} className="pw-chart-col">
          <span className={`pw-chart-bar ${value < 100 ? 'is-below' : ''}`} style={{ height: index < bars ? `${(value / max) * 100}%` : '0%' }}>{index < bars && <em>{value}%</em>}</span>
        </div>)}
      </div>
      <div className="pw-chart-labels">{scene.series.map(([label]) => <span key={label}>{label}</span>)}</div>
    </figure>}
    {at(progress, 0.74) && <ul className="pw-list pw-enter">{scene.insights.map(item => <li key={item}>{item}</li>)}</ul>}
    {at(progress, 0.8) && <div className="pw-output pw-enter"><span className="pw-file-icon is-xlsx"><FileSpreadsheet size={16} /></span><span><strong>{scene.output}</strong><small>XLSX · 24 KB</small></span><Download size={15} /></div>}
  </div>;
}

function PluginBody({ scene, copy, progress }: { scene: PluginScene; copy: LandingCopy; progress: number }) {
  const approvalAt = 0.4, clickAt = 0.5, doneAt = 0.56;
  const approved = at(progress, clickAt);
  const steps = at(progress, doneAt) ? scene.steps : scene.steps.slice(0, 2);
  return <>
    <Timeline steps={steps} progress={progress} copy={copy} start={T.stepsStart} end={0.34} />
    {at(progress, approvalAt) && progress < T.answer && <div className={`pw-approval pw-enter ${approved ? 'is-approved' : ''}`}>
      <div className="pw-approval-label"><ShieldCheck size={14} />{scene.approval.label}</div>
      <strong>{scene.approval.title}</strong>
      <span className="pw-approval-preview">{scene.approval.preview}</span>
      <dl>{scene.approval.fields.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
      <div className="pw-approval-actions"><span className="pw-btn is-dark">{approved && <Check size={14} />}{scene.approval.run}</span><span className="pw-btn">{scene.approval.deny}</span></div>
      {!approved && at(progress, approvalAt + 0.03) && <MousePointer2 className="pw-cursor" size={20} fill="#0d0d0d" stroke="#fff" strokeWidth={1.4} />}
    </div>}
    {at(progress, T.answer) && <div className="pw-answer">
      <p className="pw-enter">{scene.lead}</p>
      <div className="pw-issues">{scene.issues.slice(0, revealed(progress, scene.issues.length, 0.64, 0.72)).map(([key, title]) => <div key={key} className="pw-issue pw-enter">
        <img src="/logo/atlassian.png" alt="" width="18" height="18" /><code>{key}</code><span>{title}</span><img className="pw-issue-source" src="/logo/slack.png" alt="" width="14" height="14" />
      </div>)}</div>
    </div>}
  </>;
}

/* ------------------------------------------------------------------ */
/* Document scene                                                       */
/* ------------------------------------------------------------------ */

function DocumentPage({ scene, progress }: { scene: DocumentScene; progress: number }) {
  const { doc } = scene;
  const columnAdded = at(progress, 0.54);
  const riskAdded = at(progress, 0.58);
  // After the edits land, the editor scrolls so the added paragraph is in view.
  return <div className="pw-page" style={{ transform: at(progress, 0.62) ? 'translateY(-170px)' : undefined }}>
    <h1>{doc.title}</h1>
    <h2>{doc.overview[0]}</h2><p>{doc.overview[1]}</p>
    <h2>{doc.market[0]}</h2><ul>{doc.market[1].map(item => <li key={item}>{item}</li>)}</ul>
    <h2>{doc.goals[0]}</h2><p>{doc.goals[1]}</p>
    <table className={columnAdded ? 'has-added' : ''}>
      <thead><tr>{doc.table.head.map(cell => <th key={cell}>{cell}</th>)}{columnAdded && <th className="is-added">{doc.addedHead}</th>}</tr></thead>
      <tbody>{doc.table.rows.map((row, i) => <tr key={row[0]}>{row.map(cell => <td key={cell}>{cell}</td>)}{columnAdded && <td className="is-added">{doc.addedCells[i]}</td>}</tr>)}</tbody>
    </table>
    <h2>{doc.plan[0]}</h2><ul>{doc.plan[1].map(item => <li key={item}>{item}</li>)}</ul>
    <h2>{doc.risk[0]}</h2><p>{doc.risk[1]}</p>
    {riskAdded && <p className="is-added pw-enter">{doc.addedRisk}</p>}
  </div>;
}

function DocumentView({ scene, copy, progress, layout }: { scene: DocumentScene; copy: LandingCopy; progress: number; layout: Layout }) {
  const sent = at(progress, T.send);
  const running = sent && progress < T.answer + 0.1;
  const thread = useStickToBottom(progress);
  return <div className="pw-doc">
    <div className="pw-doc-header">
      <span className="pw-round is-square"><ArrowLeft size={17} /></span>
      <div className="pw-doc-title"><strong>{scene.fileName}<Pencil size={13} /></strong><small>{copy.ui.docAgent}<em>DOCX</em><Check size={12} />{copy.ui.saved}</small></div>
      {layout !== 'narrow' && <span className="pw-outline-btn"><Download size={14} />{copy.ui.export}</span>}
    </div>
    <div className="pw-editor">
      {layout !== 'narrow' && <div className="pw-editor-menus">{copy.ui.menus.map(menu => <span key={menu}>{menu}<ChevronDown size={12} /></span>)}</div>}
      {layout !== 'narrow' && <div className="pw-editor-tools" aria-hidden="true">
        <RotateCcw size={15} /><RotateCcw size={15} className="is-flipped" /><i />
        <span>{copy.ui.fit}<ChevronDown size={12} /></span><span className="is-wide">{copy.ui.paragraph}<ChevronDown size={12} /></span><span>Pretendard<ChevronDown size={12} /></span><i />
        <Minus size={14} /><b className="pw-font-size">11</b><Plus size={14} /><i />
        <b>B</b><b className="is-italic">I</b><b className="is-underline">U</b><b className="is-strike">S</b><i />
        <b className="is-color">A</b>
      </div>}
      <div className="pw-canvas"><DocumentPage scene={scene} progress={progress} /></div>
    </div>
    <div className="pw-doc-chat">
      <div className="pw-doc-chat-head"><span className="pw-file-icon is-docx"><FileText size={15} /></span><span><strong>{copy.ui.docAgent}</strong><small>{scene.fileName}</small></span><span className="pw-doc-chat-actions"><SquarePen size={14} /><History size={14} />{layout !== 'narrow' && <Maximize2 size={14} />}<X size={15} /></span></div>
      <div className="pw-doc-thread" ref={thread}>
        {sent && <div className="pw-user pw-enter"><div className="pw-bubble">{scene.prompt}</div></div>}
        {at(progress, T.stepsStart - 0.01) && <AgentMessage>
          <Timeline steps={scene.steps} progress={progress} copy={copy} end={0.44} />
          {at(progress, T.answer) && <div className="pw-answer">
            <p className="pw-enter">{scene.reply.lead}</p>
            {at(progress, 0.64) && <h4 className="pw-enter">{scene.reply.heading}</h4>}
            {at(progress, 0.66) && <ol className="pw-list is-ordered pw-enter">{scene.reply.items.map(item => <li key={item}>{item}</li>)}</ol>}
            {at(progress, 0.7) && <p className="pw-enter pw-muted">{scene.reply.closing}</p>}
          </div>}
        </AgentMessage>}
      </div>
      <Composer copy={copy} text={typed(scene.prompt, progress)} state={running ? 'running' : !sent && progress > 0.005 ? 'typing' : 'idle'} compact />
    </div>
  </div>;
}

/* ------------------------------------------------------------------ */
/* Window                                                              */
/* ------------------------------------------------------------------ */

function Sidebar({ copy, active }: { copy: LandingCopy; active: 'agent' | 'doc' }) {
  const ui = copy.ui;
  const item = (key: string, label: string, icon: React.ReactNode, isActive = false) => <div key={key} className={`pw-nav-item ${isActive ? 'is-active' : ''}`}><span className="pw-nav-tile">{icon}</span>{label}</div>;
  const section = (label: string) => <div className="pw-nav-section">{label}<ChevronUp size={14} /></div>;
  return <aside className="pw-sidebar" aria-hidden="true">
    <div className="pw-sidebar-head"><img src="/app_name.png" alt="" width="104" height="25" /><PanelLeftClose size={17} /></div>
    <div className="pw-segment"><span className="is-active">{ui.personal}</span><span>{ui.team}</span></div>
    {section(ui.workspace)}
    {item('a', ui.agent, 'A', active === 'agent')}{item('l', ui.live, 'L')}{item('d', ui.docAgent, 'D', active === 'doc')}
    {section(ui.teamWorkspace)}
    {item('team', ui.teamManage, <ShieldCheck size={14} />)}
    {section(ui.personalSection)}
    {item('w', ui.widget, <LayoutGrid size={14} />)}{item('f', ui.feed, <Newspaper size={14} />)}{item('t', ui.tools, <Wrench size={14} />)}{item('m', ui.dm, <Send size={14} />)}
    {section(ui.manage)}
    {item('s', ui.storage, <Folder size={14} />)}{item('p', ui.plugin, <Puzzle size={14} />)}{item('b', ui.memory, <Brain size={14} />)}
    <div className="pw-profile"><span>{ui.profile.slice(0, 1)}</span><div><strong>{ui.profile}</strong><small>{ui.profileSub}</small></div><MoreHorizontal size={15} /></div>
  </aside>;
}

function Scene({ scene, copy, progress, layout }: { scene: SceneCopy; copy: LandingCopy; progress: number; layout: Layout }) {
  if (scene.id === 'document') return <DocumentView scene={scene} copy={copy} progress={progress} layout={layout} />;
  const body = scene.id === 'plugin'
    ? <PluginBody scene={scene} copy={copy} progress={progress} />
    : <><Timeline steps={scene.steps} progress={progress} copy={copy} />{at(progress, T.answer) && (scene.id === 'research' ? <ResearchResult scene={scene} progress={progress} /> : <DataResult scene={scene} progress={progress} />)}</>;
  return <ChatScene scene={scene} copy={copy} progress={progress} layout={layout}><AgentMessage>{body}</AgentMessage></ChatScene>;
}

export function ProductWindow({ copy, scene, progress, layout }: { copy: LandingCopy; scene: SceneCopy; progress: number; layout: Layout }) {
  return <div className={`pw is-${layout}`} data-scene={scene.id}>
    {/* On the wide layout the scene tabs sit in this bar (see HeroDemo), so the URL steps aside. */}
    <div className="pw-titlebar"><span className="pw-lights"><i /><i /><i /></span>{layout !== 'wide' && <span className="pw-url">{copy.demo.url}{scene.id === 'document' ? '/doc-agent' : '/agents'}</span>}<span className="pw-lights is-ghost" /></div>
    <div className="pw-body">
      {layout === 'wide' && <Sidebar copy={copy} active={scene.id === 'document' ? 'doc' : 'agent'} />}
      <div className="pw-main"><Scene scene={scene} copy={copy} progress={progress} layout={layout} /></div>
    </div>
  </div>;
}

export function PlaybackButtons({ playback, copy }: { playback: Playback; copy: LandingCopy }) {
  return <div className="landing-playback">
    <button type="button" aria-label={playback.paused ? copy.demo.play : copy.demo.pause} aria-pressed={!playback.paused} onClick={playback.toggle}>{playback.paused ? <Play size={13} fill="currentColor" /> : <Pause size={13} fill="currentColor" />}</button>
    <button type="button" aria-label={copy.demo.replay} onClick={() => { playback.restart(); if (playback.paused) playback.toggle(); }}><RotateCcw size={13} /></button>
  </div>;
}

const SCENE_DURATION = 15000;
const SCENE_ICONS = [BarChart3, FileText, FileSpreadsheet, Puzzle];

export function HeroDemo({ copy }: { copy: LandingCopy }) {
  const [selected, setSelected] = useState(0);
  const count = copy.scenes.length;
  const next = useCallback(() => setSelected(value => (value + 1) % count), [count]);
  const playback = usePlayback(SCENE_DURATION, next);
  const fit = useFit();
  const tabId = useId();
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const select = (index: number) => { setSelected(index); playback.restart(playback.paused); };
  const scene = copy.scenes[selected];
  const { width, height } = LAYOUTS[fit.layout];
  return <div className="landing-product" ref={playback.ref} data-playing={playback.running} data-layout={fit.layout} id="product-demo" style={{ '--scale': fit.scale } as React.CSSProperties}>
    <div className="landing-demo-bar">
      <div className="landing-demo-tabs" role="tablist" aria-label={copy.demo.label}>{copy.scenes.map((item, i) => {
        const Icon = SCENE_ICONS[i];
        return <button key={item.id} type="button" ref={node => { tabs.current[i] = node; }} id={`${tabId}-tab-${i}`} aria-controls={`${tabId}-panel`} role="tab" aria-selected={selected === i} tabIndex={selected === i ? 0 : -1} onClick={() => select(i)} onKeyDown={event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const index = event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : (selected + (event.key === 'ArrowRight' ? 1 : count - 1)) % count;
          select(index); tabs.current[index]?.focus();
        }}><Icon size={15} strokeWidth={1.9} /><span>{item.tab}</span>{selected === i && <i className="landing-tab-progress" style={{ transform: `scaleX(${playback.progress})` }} />}</button>;
      })}</div>
      <PlaybackButtons playback={playback} copy={copy} />
    </div>
    <div className="landing-window" ref={fit.ref} id={`${tabId}-panel`} role="tabpanel" aria-labelledby={`${tabId}-tab-${selected}`}>
      <p className="landing-sr-only">{scene.prompt}</p>
      <div className="landing-window-frame" style={{ height: height * fit.scale }}>
        <div className="landing-scene" data-phase={phaseOf(playback.progress)} data-scene={scene.id} aria-hidden="true" style={{ width, height, zoom: fit.scale } as React.CSSProperties}>
          <ProductWindow key={`${scene.id}-${fit.layout}`} copy={copy} scene={scene} progress={playback.progress} layout={fit.layout} />
        </div>
      </div>
    </div>
  </div>;
}
